// Contexto global de autenticação: guarda o token e o usuário logado, e expõe
// login/register/logout/changePassword para o resto do app. Token e usuário são
// persistidos no localStorage para sobreviver a um F5 (ver useState inicializado
// a partir do localStorage abaixo).
import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { carregarLabelsAreas } from '../utils/areas';
import { API_URL } from '../api/config';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('authToken'));
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('authUser');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      // localStorage corrompido/JSON inválido: trata como se não houvesse usuário salvo
      return null;
    }
  });
  const [loadingAuth, setLoadingAuth] = useState(Boolean(token));
  const [hasMaster, setHasMaster] = useState(false);

  // Mantém o header Authorization do axios sempre sincronizado com o token atual,
  // para que todo `axios.get/post(...)` feito em qualquer lugar do app já saia autenticado.
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common.Authorization;
    }
  }, [token]);

  // Revalida a sessão salva contra o backend ao carregar o app: se o token salvo
  // expirou ou foi invalidado, faz logout automaticamente (ver .catch abaixo).
  useEffect(() => {
    if (!token) {
      setLoadingAuth(false);
      return;
    }

    axios
      .get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const payload = res.data?.data ?? res.data;
        if (payload?.user) {
          setUser(payload.user);
          localStorage.setItem('authUser', JSON.stringify(payload.user));
        }
      })
      .catch(() => {
        logout();
      })
      .finally(() => setLoadingAuth(false));

    // Nomes de exibição das áreas (Educação por Princípios, Esporte etc.) —
    // não é dado de sessão, mas aproveita o mesmo gatilho (token disponível)
    // pra já carregar antes de qualquer tela que os mostre (Turmas, Ponto,
    // Grade por Turma...). Ver utils/areas.js.
    carregarLabelsAreas(token);
  }, [token]);

  // Usado pela tela de login/registro para saber se já existe um usuário master no
  // sistema (define se deve oferecer o fluxo de "criar o primeiro master" ou o login normal).
  useEffect(() => {
    axios
      .get(`${API_URL}/api/auth/has-master`)
      .then((res) => {
        const payload = res.data?.data ?? res.data;
        setHasMaster(payload?.hasMaster ?? false);
      })
      .catch((err) => {
        console.error('Erro ao verificar master:', err);
        setHasMaster(false);
      });
  }, []);

  const saveSession = (data) => {
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('authToken', data.token);
    localStorage.setItem('authUser', JSON.stringify(data.user));
  };

  const login = async (email, senha) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, senha });
    const payload = response.data?.data ?? response.data;
    saveSession(payload);
    return payload;
  };

  // Login de aluno via código de acesso (ver AlunoLogin.js) — mesmo formato de
  // sessão (token + user), só que `user` tem perfil: 'aluno' e aluno_id em vez
  // de id de usuários (ver POST /api/auth/aluno-login no backend).
  const loginAluno = async (codigo_acesso) => {
    const response = await axios.post(`${API_URL}/api/auth/aluno-login`, { codigo_acesso });
    const payload = response.data?.data ?? response.data;
    saveSession(payload);
    return payload;
  };

  const register = async (nome, email, senha, perfil, id_instituicao) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      nome,
      email,
      senha,
      perfil,
      id_instituicao,
    });
    const payload = response.data?.data ?? response.data;
    // Cadastro de usuário comum fica "pendente" (sem token) até um master aprovar —
    // só salva sessão se o backend já devolveu um token (caso do primeiro master).
    if (payload?.token && payload?.user) {
      saveSession(payload);
    }
    return payload;
  };

  const changePassword = async (senhaAtual, novaSenha) => {
    const response = await axios.post(
      `${API_URL}/api/auth/change-password`,
      { senhaAtual, novaSenha },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data?.data ?? response.data;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    delete axios.defaults.headers.common.Authorization;
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        loadingAuth,
        hasMaster,
        isAuthenticated: Boolean(token && user),
        login,
        loginAluno,
        register,
        changePassword,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
